if (!window.cartVariantTracker) {
  class CartVariantTracker {
    constructor() {
      this.variants = new Set();
      this.forms = new Set();
      this.isRefreshing = false;
      this.init();
    }

    init() {
      if (Array.isArray(window.cartVariantIds)) {
        this.setVariantsFromIds(window.cartVariantIds);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
      }

      document.addEventListener('cart:updated', (e) => this.handleCartUpdate(e.detail));
      document.addEventListener('cart:refresh', () => this.refresh());

      this.refresh();
    }

    setVariantsFromIds(ids) {
      this.variants.clear();
      if (Array.isArray(ids)) {
        ids.forEach((id) => {
          const numId = Number(id);
          if (numId) this.variants.add(numId);
        });
      }
      this.notifyForms();
    }

    setVariantsFromItems(items) {
      this.variants.clear();
      if (Array.isArray(items)) {
        items.forEach((item) => {
          const vid = Number(item.variant_id || item.id);
          if (vid && Number(item.quantity) > 0) {
            this.variants.add(vid);
          }
        });
      }
      this.notifyForms();
    }

    refresh() {
      if (this.isRefreshing) return Promise.resolve();
      this.isRefreshing = true;
      const cartUrl = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
      return fetch(cartUrl)
        .then((res) => (res.ok ? res.json() : null))
        .then((cart) => {
          if (cart && Array.isArray(cart.items)) {
            this.setVariantsFromItems(cart.items);
          }
        })
        .catch((err) => {
          console.warn('[CartVariantTracker] Refresh error:', err);
        })
        .finally(() => {
          this.isRefreshing = false;
        });
    }

    register(form) {
      this.forms.add(form);
    }

    unregister(form) {
      this.forms.delete(form);
    }

    has(variantId) {
      return this.variants.has(Number(variantId));
    }

    add(variantId) {
      const numId = Number(variantId);
      if (!numId) return;
      this.variants.add(numId);
      this.notifyForms();
    }

    remove(variantId) {
      const numId = Number(variantId);
      if (!numId) return;
      this.variants.delete(numId);
      this.notifyForms();
    }

    handleCartUpdate(event) {
      if (!event) {
        this.refresh();
        return;
      }

      if (event.cartData && Array.isArray(event.cartData.items)) {
        this.setVariantsFromItems(event.cartData.items);
      } else if (event.source === 'product-form' && event.productVariantId) {
        this.add(event.productVariantId);
        this.refresh();
      } else {
        this.refresh();
      }
    }

    notifyForms() {
      this.forms.forEach((form) => {
        if (typeof form.updateCartState === 'function') {
          form.updateCartState();
        }
      });
    }
  }

  window.cartVariantTracker = new CartVariantTracker();
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector(
            'span:not(.product-card-cart-icon):not(.sold-out-message):not(.icon-wrap):not(.loading__spinner)'
          ) || this.submitButton.querySelector('span')
          : null;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (this.isEligibleForm) {
          if (!window.cartVariantTracker) {
            window.cartVariantTracker = new CartVariantTracker();
          }
          window.cartVariantTracker.register(this);
          this.initVariantChangeListeners();
          this.updateCartState();
        }
      }

      disconnectedCallback() {
        if (window.cartVariantTracker) {
          window.cartVariantTracker.unregister(this);
        }
        if (typeof this.variantChangeUnsubscriber === 'function') {
          this.variantChangeUnsubscriber();
          this.variantChangeUnsubscriber = null;
        }
      }

      get isEligibleForm() {
        if (!this.submitButton) return false;
        if (this.closest('quick-add-modal')) return false;
        return Boolean(
          this.submitButton.classList.contains('quick-add__submit') ||
          this.submitButton.classList.contains('product-form__submit') ||
          this.submitButton.classList.contains('shop-the-look__btn-add') ||
          this.closest(
            '.card-wrapper, .product-card-wrapper, .card, .bse-card, .quick-add, .tmg-cart, .product__info-container, .product'
          )
        );
      }

      initVariantChangeListeners() {
        this.form?.addEventListener('change', () => {
          this.updateCartState();
        });

        // Swatches on product cards
        const card = this.closest('.card-wrapper, .product-card-wrapper, .card, .bse-card, .tabbed-mega-menu__product');
        const swatches = card?.querySelector('product-card-swatches');
        if (swatches) {
          swatches.addEventListener('click', () => {
            setTimeout(() => this.updateCartState(), 10);
          });
        }

        // Variant changes on PDP (e.g. from variant picker / product-info.js)
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = this.dataset.sectionId || this.dataset.section;
            if (!event?.data || !sectionId || event.data.sectionId === sectionId) {
              setTimeout(() => this.updateCartState(), 10);
            }
          });
        }
      }

      updateCartState() {
        if (!this.isEligibleForm || !this.submitButton) return;

        const currentVariantId = Number(this.variantIdInput?.value);
        const isInCart = window.cartVariantTracker ? window.cartVariantTracker.has(currentVariantId) : false;
        const textSpan = this.submitButtonText;
        if (!textSpan) return;

        if (!textSpan.dataset.originalText) {
          const initialText = textSpan.textContent.trim();
          if (initialText && initialText !== 'Added to Cart') {
            textSpan.dataset.originalText = initialText;
          } else {
            textSpan.dataset.originalText = window.variantStrings?.addToCart || 'Add to cart';
          }
        }

        if (isInCart) {
          textSpan.textContent = 'Added to Cart';
          this.submitButton.classList.add('is-added');
        } else {
          textSpan.textContent = textSpan.dataset.originalText || window.variantStrings?.addToCart || 'Add to cart';
          this.submitButton.classList.remove('is-added');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            if (window.cartVariantTracker) {
              window.cartVariantTracker.add(formData.get('id'));
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (this.isEligibleForm) {
              this.updateCartState();
            }

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          this.submitButton.classList.remove('is-added');
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          if (this.isEligibleForm) {
            this.updateCartState();
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
